import { sortMetadataEntries } from "../utils/DisplayHelpers";
import LinkHierarchyHelper from "../../link/LinkHierarchyHelper";
import { TrailblazerRouteHopType } from "./TrailblazerTypes";
import DataTransformer from "../utils/DataTransformer";

/**
 * Abstract class that represents the RouteHop.
 * Each concrete class should specify how to display the data for the RouteHop.
 */
export class TrailblazerRouteHop {
    constructor(data) {
        this.name = data.name;
        this.metadata = data.metadata;
        this.position = data.position;
    }

    /**
     * Gets the displayable metadata key-value pairs.
     */
    getDisplayableMetadata = () => {
        const transformed = {};
        Object.entries(this.constructor.METADATA_INFORMATION).forEach(([targetField, config]) => {
            const value = config.displayTransformer(this);
            if (value !== null && value !== undefined) {
                transformed[targetField] = value;
            }
        });
        return transformed;
    }

    toRenderableMetadata = () => {
        const displayableMetadata = this.getDisplayableMetadata();
        return DataTransformer.transformToRenderable(displayableMetadata, this.constructor.METADATA_INFORMATION);
    }

    /**
     * Returns the node label as a string.
     */
    toCytoscapeLabelString = () => {
        const displayableMetadata = this.getDisplayableMetadata();
        const prefix = this.getLabelPrefix();

        if (Object.keys(displayableMetadata).length === 0) {
            return `${prefix}: ${this.name}\nNo metadata`;
        }

        const sortedMetadata = sortMetadataEntries(displayableMetadata, this.constructor.METADATA_INFORMATION);
        const metadataString = sortedMetadata
            .map(([key, value]) => `${this.constructor.METADATA_INFORMATION[key].displayName}: ${value}`)
            .join("\n");

        return `${prefix}: ${this.name}\n${metadataString}`;
    }

    /**
     * Returns a top-level data object with all fields from the RouteHop
     */
    toDataObject = () => ({
        name: this.name,
        position: this.position,
        ...this.metadata,
        ...this.getDisplayableMetadata()
    })

    /**
     * Returns the data object readable by the Table.
     */
    toTableData = () => {
        const baseData = {
            name: this.name,
            position: this.position,
            ...this.getDisplayableMetadata()
        };
        return DataTransformer.transformForTable(baseData, this.constructor.METADATA_INFORMATION);
    }

    getLabelPrefix = () => { throw new Error("method 'getLabelPrefix()' must be implemented."); }
}

/**
 * Device RouteHop extending the TrailblazerNodeData abstract class.
 * A device data object is expected to represent a RouteHop from the API response.
 */
export class DeviceRouteHop extends TrailblazerRouteHop {
    /**
     * Stores information about the metadata fields, like how it is displayed and how the value is rendered.
     * Properties of each configuration object:
     * displayName - name to display the field with
     * priority - priority of field (ordering for table, etc.)
     * displayTransformer - function to extract the value from the DeviceRouteHop instance
     */
    static METADATA_INFORMATION = {
        position: {
            displayName: "Hop #",
            priority: 1,
            displayTransformer: instance => instance.position
        },
        deviceName: {
            displayName: "Device Name",
            priority: 2,
            displayTransformer: instance => instance.metadata?.deviceName || null
        },
        interfaceName: {
            displayName: "Interface Name",
            priority: 3,
            displayTransformer: instance => instance.metadata?.interfaceName || null
        },
        linkId: {
            displayName: "Link ID",
            priority: 4,
            displayTransformer: instance => instance.metadata?.linkId || null
        },
        lifecycleState: {
            displayName: "Lifecycle State",
            priority: 4,
            displayTransformer: instance => instance.metadata?.lifecycleState || null
        },
        linkType: {
            displayName: "Link Type",
            priority: 5,
            displayTransformer: instance => (instance.metadata?.typeId ?
                LinkHierarchyHelper.getReadableLinkTypeFromTypeIdAndStage(instance.metadata.typeId) : null)
        }
    };

    getLabelPrefix = () => "NAME"
}

/**
 * Error RouteHop for representing errors during traversal.
 */
export class ErrorRouteHop extends TrailblazerRouteHop {
    static METADATA_INFORMATION = {
        position: {
            displayName: "Hop #",
            priority: 1,
            displayTransformer: instance => instance.position
        },
        message: {
            displayName: "Error Message",
            priority: 2,
            displayTransformer: instance => instance.metadata?.message || null
        }
    };

    getLabelPrefix = () => "ERROR"
}

/**
 * System RouteHop for representing system summaries with multiple devices.
 */
export class SystemRouteHop extends TrailblazerRouteHop {
    static METADATA_INFORMATION = {
        position: {
            displayName: "Hop #",
            priority: 1,
            displayTransformer: instance => instance.position
        },
        devices: {
            displayName: "Devices",
            priority: 2,
            displayTransformer: instance => instance.metadata?.devices || null
        }
    };

    getLabelPrefix = () => "SYSTEM"
}

/**
 * Factor to handle constructing all hop types.
 */
export class RouteHopFactory {
    static createHop = (hopData) => {
        const hopType = hopData?.metadata?.type;

        if (hopType === TrailblazerRouteHopType.DEVICE) {
            return new DeviceRouteHop(hopData);
        } else if (hopType === TrailblazerRouteHopType.ERROR) {
            return new ErrorRouteHop(hopData);
        } else if (hopType === TrailblazerRouteHopType.SYSTEM) {
            return new SystemRouteHop(hopData);
        }
        throw new Error(`Unknown hop type: ${hopType}`);
    }
}